import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { Selection, TextSelection } from "@tiptap/pm/state";
import { Node as PMNode } from "@tiptap/pm/model";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    replaceText: {
      /**
       * Hide or show every {{…}} placeholder. View-only: the placeholders stay
       * in the document (and in what gets saved), so showing them again brings
       * every one back. Exports strip them via stripPlaceholders().
       * `animate` fades them out before collapsing, or back in when shown;
       * leave it off for the initial sync on load.
       */
      setPlaceholdersHidden: (hidden: boolean, animate?: boolean) => ReturnType;
    };
  }
}

export const replaceTextPluginKey = new PluginKey("replaceText");

const PLACEHOLDER_RE = /\{\{[^}]+\}\}/g;

/**
 * Remove every {{…}} placeholder from exported HTML or plain text, tidying the
 * gap it leaves: a space before punctuation or another space, and paragraphs
 * or list items that held nothing but placeholders.
 */
export const stripPlaceholders = (content: string): string => {
  let out = content
    .replace(PLACEHOLDER_RE, "")
    .replace(/[ \t]+([.,;:!?)])/g, "$1")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/[ \t]+(?=\n|<br|<\/(?:p|li|h[1-6])>)/g, "")
    .replace(/\n{3,}/g, "\n\n");
  // Emptied blocks nest (<ul><li><p></p></li></ul>), so peel until stable.
  const emptyBlock =
    /<(p|h[1-6]|li|ul|ol)[^>]*>(\s|&nbsp;|<br\s*\/?>)*<\/\1>/gi;
  for (let prev = ""; prev !== out; ) {
    prev = out;
    out = out.replace(emptyBlock, "");
  }
  return out;
};

interface ReplaceTextRange {
  from: number;
  to: number;
  text: string;
  /** Document positions of hard breaks within the inner content (between {{ and }}) */
  hardBreakPositions: number[];
  /** What collapses while placeholders are hidden: the range plus a dangling space */
  hideFrom: number;
  hideTo: number;
}

/** A hide/show animation in flight: "leave" fades out, "enter" fades in */
type ReplaceTextPhase = "leave" | "enter" | null;

interface ReplaceTextState {
  ranges: ReplaceTextRange[];
  /** Textblocks that hold nothing but placeholders; collapsed while hidden */
  emptyBlocks: { from: number; to: number }[];
  hidden: boolean;
  phase: ReplaceTextPhase;
  decorationSet: DecorationSet;
}

interface ReplaceTextMeta {
  hidden?: boolean;
  phase?: ReplaceTextPhase;
}

// Keep in sync with .replace-text-leave / .replace-text-enter in index.css.
const LEAVE_MS = 260;
const ENTER_MS = 300;

/**
 * ReplaceText Extension
 *
 * This extension finds text wrapped in double brackets {{text}} and:
 * 1. Applies visual styling (yellow background, dashed border)
 * 2. Makes them clickable to select all text within (including brackets)
 * 3. Replaces the entire bracketed text with plain text when user starts typing
 */
export interface ReplaceTextOptions {
  /** When true, clicking a placeholder selects the entire {{…}} range. Default: true */
  selectOnClick: boolean;
}

interface ReplaceTextStorage {
  phaseTimer: ReturnType<typeof setTimeout> | null;
}

export const ReplaceText = Extension.create<ReplaceTextOptions, ReplaceTextStorage>({
  name: "replaceText",
  // Runs ahead of the core keymap so Backspace/Delete beside a hidden
  // placeholder can step over it before the default handler deletes a "}".
  priority: 1000,

  addOptions() {
    return {
      selectOnClick: true,
    };
  },

  addStorage() {
    return { phaseTimer: null };
  },

  onDestroy() {
    if (this.storage.phaseTimer) clearTimeout(this.storage.phaseTimer);
  },

  addProseMirrorPlugins() {
    const { selectOnClick } = this.options;

    // Find all {{text}} patterns in the document, spanning across hard breaks
    const findReplacementRanges = (
      doc: PMNode,
    ): Pick<ReplaceTextState, "ranges" | "emptyBlocks"> => {
      const ranges: ReplaceTextRange[] = [];
      const emptyBlocks: ReplaceTextState["emptyBlocks"] = [];
      const regex = /\{\{([^}]+)\}\}/g;

      doc.descendants((node, pos) => {
        if (!node.isTextblock) return;

        // Build combined text and position map for this block
        let combinedText = "";
        const posMap: number[] = [];
        const hardBreaks: number[] = [];

        node.forEach((child, offset) => {
          if (child.isText && child.text) {
            for (let i = 0; i < child.text.length; i++) {
              posMap.push(pos + 1 + offset + i);
            }
            combinedText += child.text;
          } else if (child.type.name === "hardBreak") {
            const hbPos = pos + 1 + offset;
            posMap.push(hbPos);
            hardBreaks.push(hbPos);
            combinedText += "\n";
          }
        });

        let match;
        let found = false;
        while ((match = regex.exec(combinedText)) !== null) {
          found = true;
          const start = match.index;
          const end = start + match[0].length;
          const from = posMap[start];
          const to = posMap[end - 1] + 1;
          const innerFrom = from + 2;
          const innerTo = to - 2;
          // "at {{time}}." should read "at." once hidden, not "at .": take the
          // space before the placeholder when what follows is a space,
          // punctuation, or the end of the line.
          const next = combinedText[end];
          const swallowSpace =
            combinedText[start - 1] === " " &&
            (next === undefined || /[\s.,;:!?)]/.test(next));
          ranges.push({
            from,
            to,
            text: match[1],
            hardBreakPositions: hardBreaks.filter(
              (p) => p >= innerFrom && p < innerTo
            ),
            hideFrom: swallowSpace ? from - 1 : from,
            hideTo: to,
          });
        }

        if (found && !combinedText.replace(regex, "").trim()) {
          emptyBlocks.push({ from: pos, to: pos + node.nodeSize });
        }

        return false; // don't descend into children
      });

      return { ranges, emptyBlocks };
    };

    const buildDecorations = (
      doc: PMNode,
      { ranges, emptyBlocks }: Pick<ReplaceTextState, "ranges" | "emptyBlocks">,
      hidden: boolean,
      phase: ReplaceTextPhase = null
    ): DecorationSet => {
      const decorations: Decoration[] = [];

      if (hidden) {
        ranges.forEach((range) => {
          decorations.push(
            Decoration.inline(range.hideFrom, range.hideTo, {
              class: "replace-text-hidden",
              nodeName: "span",
            })
          );
        });
        emptyBlocks.forEach((block) => {
          decorations.push(
            Decoration.node(block.from, block.to, {
              class: "replace-text-hidden-block",
            })
          );
        });
        return DecorationSet.create(doc, decorations);
      }

      const chipClass = phase
        ? `replace-text replace-text-${phase}`
        : "replace-text";
      // A paragraph of only placeholders fades as a whole, since it collapses
      // as a whole once hidden.
      if (phase) {
        emptyBlocks.forEach((block) => {
          decorations.push(
            Decoration.node(block.from, block.to, {
              class: `replace-text-${phase}-block`,
            })
          );
        });
      }

      ranges.forEach((range) => {
        // The space that collapses with a hidden placeholder shrinks (or
        // grows back) with it, so the sentence closes up smoothly.
        if (phase && range.hideFrom < range.from) {
          decorations.push(
            Decoration.inline(range.hideFrom, range.from, {
              class: `replace-text-${phase}-space`,
              nodeName: "span",
            })
          );
        }

        // Hide the opening brackets {{
        decorations.push(
          Decoration.inline(range.from, range.from + 2, {
            class: "replace-text-bracket",
            nodeName: "span",
          })
        );

        // Style the text content (without brackets), splitting at hard breaks
        const innerFrom = range.from + 2;
        const innerTo = range.to - 2;

        if (range.hardBreakPositions.length === 0) {
          decorations.push(
            Decoration.inline(innerFrom, innerTo, {
              class: chipClass,
              nodeName: "span",
            })
          );
        } else {
          let segStart = innerFrom;
          for (const hbPos of range.hardBreakPositions) {
            if (hbPos > segStart) {
              decorations.push(
                Decoration.inline(segStart, hbPos, {
                  class: chipClass,
                  nodeName: "span",
                })
              );
            }
            segStart = hbPos + 1;
          }
          if (segStart < innerTo) {
            decorations.push(
              Decoration.inline(segStart, innerTo, {
                class: chipClass,
                nodeName: "span",
              })
            );
          }
        }

        // Hide the closing brackets }}
        decorations.push(
          Decoration.inline(range.to - 2, range.to, {
            class: "replace-text-bracket",
            nodeName: "span",
          })
        );
      });

      return DecorationSet.create(doc, decorations);
    };

    return [
      new Plugin({
        key: replaceTextPluginKey,
        state: {
          init(_, { doc }): ReplaceTextState {
            const found = findReplacementRanges(doc);
            return {
              ...found,
              hidden: false,
              phase: null,
              decorationSet: buildDecorations(doc, found, false),
            };
          },
          apply(tr, value, oldState, newState): ReplaceTextState {
            const meta = tr.getMeta(replaceTextPluginKey) as
              | ReplaceTextMeta
              | undefined;
            const hidden = meta?.hidden ?? value.hidden;
            const phase = meta?.phase !== undefined ? meta.phase : value.phase;

            if (
              !tr.docChanged &&
              hidden === value.hidden &&
              phase === value.phase
            ) {
              return {
                ...value,
                decorationSet: value.decorationSet.map(tr.mapping, tr.doc),
              };
            }

            const found = tr.docChanged
              ? findReplacementRanges(newState.doc)
              : value;
            return {
              ranges: found.ranges,
              emptyBlocks: found.emptyBlocks,
              hidden,
              phase,
              decorationSet: buildDecorations(
                newState.doc,
                found,
                hidden,
                phase
              ),
            };
          },
        },

        // While hidden, keep the caret out of the collapsed text: arrow keys,
        // clicks, and edits that would park it inside a placeholder (where
        // typing would land invisibly) push it to the nearest visible edge.
        appendTransaction(_trs, oldState, newState) {
          const pluginState = replaceTextPluginKey.getState(
            newState
          ) as ReplaceTextState;
          if (!pluginState?.hidden) return null;

          const { selection, doc } = newState;
          const dir = selection.head >= oldState.selection.head ? 1 : -1;

          const resolveHead = (head: number): number => {
            for (let guard = 0; guard < 20; guard++) {
              const range = pluginState.ranges.find(
                (r) => head > r.hideFrom && head < r.hideTo
              );
              if (range) {
                head = dir > 0 ? range.hideTo : range.hideFrom;
                continue;
              }
              const block = pluginState.emptyBlocks.find(
                (b) => head > b.from && head < b.to
              );
              if (block) {
                const edge = dir > 0 ? block.to : block.from;
                const near =
                  Selection.findFrom(doc.resolve(edge), dir, true) ??
                  Selection.findFrom(
                    doc.resolve(dir > 0 ? block.from : block.to),
                    -dir,
                    true
                  );
                if (!near || near.head === head) return head;
                head = near.head;
                continue;
              }
              return head;
            }
            return head;
          };

          const head = resolveHead(selection.head);
          const anchor = selection.empty ? head : resolveHead(selection.anchor);
          if (head === selection.head && anchor === selection.anchor) {
            return null;
          }
          return newState.tr.setSelection(
            TextSelection.create(doc, anchor, head)
          );
        },

        props: {
          // Backspace right after (or Delete right before) a hidden
          // placeholder would remove one of its invisible braces. Step over
          // it first and let the default handler delete the visible character.
          handleKeyDown(view, event) {
            if (event.key !== "Backspace" && event.key !== "Delete") {
              return false;
            }
            const pluginState = replaceTextPluginKey.getState(
              view.state
            ) as ReplaceTextState;
            const { selection } = view.state;
            if (!pluginState?.hidden || !selection.empty) return false;

            let pos = selection.head;
            for (let guard = 0; guard < 20; guard++) {
              const range = pluginState.ranges.find((r) =>
                event.key === "Backspace" ? r.hideTo === pos : r.hideFrom === pos
              );
              if (!range) break;
              pos = event.key === "Backspace" ? range.hideFrom : range.hideTo;
            }
            if (pos !== selection.head) {
              view.dispatch(
                view.state.tr.setSelection(
                  TextSelection.create(view.state.doc, pos)
                )
              );
            }
            return false;
          },

          decorations(state) {
            return this.getState(state)?.decorationSet;
          },

          // Handle click events on replace-text decorations
          handleClick: selectOnClick
            ? (view, pos, event) => {
                const target = event.target as HTMLElement;
                if (!target.classList.contains("replace-text")) return false;

                const pluginState = replaceTextPluginKey.getState(view.state);
                if (!pluginState) return false;

                // Find which range was clicked
                const clickedRange = pluginState.ranges.find(
                  (range: ReplaceTextRange) =>
                    pos >= range.from && pos <= range.to
                );

                if (clickedRange) {
                  // Select the entire bracketed text
                  const tr = view.state.tr.setSelection(
                    TextSelection.create(
                      view.state.doc,
                      clickedRange.from,
                      clickedRange.to
                    )
                  );
                  view.dispatch(tr);
                  view.focus();
                  return true;
                }

                return false;
              }
            : undefined,

          // Handle input to replace bracketed text with plain text
          handleTextInput: selectOnClick
            ? (view, from, to, text) => {
                const pluginState = replaceTextPluginKey.getState(view.state);
                if (!pluginState) return false;

                const { selection } = view.state;

                // When the full range is selected (clicked on existing placeholder),
                // replace everything including brackets with plain text.
                const fullRange = pluginState.ranges.find(
                  (range: ReplaceTextRange) =>
                    selection.from === range.from && selection.to === range.to
                );

                if (fullRange) {
                  const tr = view.state.tr
                    .delete(fullRange.from, fullRange.to)
                    .insertText(text, fullRange.from);
                  view.dispatch(tr);
                  return true;
                }

                // When inner content is selected (e.g., space after Cmd+/),
                // replace just the inner content but keep the brackets.
                // Default browser behavior handles this — selected text gets replaced by typed char.
                return false;
              }
            : undefined,
        },
      }),
    ];
  },

  addCommands() {
    return {
      setPlaceholdersHidden:
        (hidden, animate = false) =>
        ({ editor, tr, dispatch }) => {
          if (!dispatch) return true;
          const storage = this.storage;
          if (storage.phaseTimer) {
            clearTimeout(storage.phaseTimer);
            storage.phaseTimer = null;
          }
          const later = (ms: number, meta: ReplaceTextMeta) => {
            storage.phaseTimer = setTimeout(() => {
              storage.phaseTimer = null;
              if (editor.isDestroyed) return;
              editor.view.dispatch(
                editor.state.tr.setMeta(replaceTextPluginKey, meta)
              );
            }, ms);
          };

          if (!animate) {
            tr.setMeta(replaceTextPluginKey, { hidden, phase: null });
          } else if (hidden) {
            // Fade the chips out while they still take space, then collapse.
            tr.setMeta(replaceTextPluginKey, { hidden: false, phase: "leave" });
            later(LEAVE_MS, { hidden: true, phase: null });
          } else {
            // The "enter" class is dropped once the fade ends so chips that
            // re-render on later edits don't fade in again.
            tr.setMeta(replaceTextPluginKey, { hidden: false, phase: "enter" });
            later(ENTER_MS, { phase: null });
          }
          return true;
        },
    };
  },
});

export default ReplaceText;
